
import { Navigate } from 'react-router-dom';
import { useRole } from '@/hooks/useRole';
import { useAuth } from '@/hooks/useAuth';
import { useMaintenanceMode } from '@/context/MaintenanceContext';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

interface AuthRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

const AuthRoute = ({ children, adminOnly = false }: AuthRouteProps) => {
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { role, isLoading: roleLoading } = useRole(user?.id);
  const { isMaintenanceMode, isLoading: maintenanceLoading } = useMaintenanceMode();
  
  // Use useMemo to calculate isAdmin only when dependencies change
  const isAdmin = useMemo(() => {
    return role === 'admin';
  }, [role]);

  // Combine all loading states into one
  const isLoading = authLoading || (user && roleLoading) || maintenanceLoading;

  // Show loading indicator only when necessary
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If no user is logged in
  if (!user) {
    return <Navigate to={isMaintenanceMode ? "/coming-soon" : "/login"} state={{ from: location }} replace />;
  }

  // If admin only route but user is not admin
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  // If in maintenance mode and user is not admin, only allow access to dashboard
  if (isMaintenanceMode && !isAdmin && location.pathname !== "/dashboard") {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default AuthRoute;
